import React, { useEffect, useState, useCallback } from "react";
import {
  FaBuilding, FaStore, FaBarcode, FaEnvelope, FaPhone, FaRegStickyNote,
  FaMapMarkerAlt, FaHome, FaClipboardList, FaHashtag, FaCity, FaListUl, FaKey
} from "react-icons/fa";
import { IMaskInput } from "react-imask";

/* ===== Auxiliares IBGE ===== */
function normalizarCidade(str) {
  return (str || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

async function buscarCodigoIBGE(cidade, uf) {
  if (!cidade || !uf) return "";
  try {
    const res = await fetch(`https://brasilapi.com.br/api/ibge/municipios/v1/${(uf || "").toLowerCase()}`);
    const lista = await res.json();
    if (Array.isArray(lista)) {
      const cidadeNormalizada = normalizarCidade(cidade);
      const item = lista.find((c) => normalizarCidade(c.nome) === cidadeNormalizada);
      return item ? item.codigo_ibge : "";
    }
  } catch {}
  return "";
}

/* ===== Campo com ícone à esquerda ===== */
const InputIcon = ({ icon, ...props }) => (
  <div className="relative">
    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-400">
      {icon}
    </span>
    {props.mask ? (
      <IMaskInput {...props} className={`pl-10 ${props.className || ""}`} />
    ) : (
      <input {...props} className={`pl-10 ${props.className || ""}`} />
    )}
  </div>
);

const regimes = [
  { value: "1", label: "1 - Simples Nacional" },
  { value: "2", label: "2 - Simples Nacional (Excesso Sublimite)" },
  { value: "3", label: "3 - Regime Normal" },
  { value: "4", label: "4 - MEI" },
];

const ModalNovaEmpresa = ({
  isOpen,
  onClose,
  onSave,
  form,
  setForm,
  erros = {},
  editandoId,
  carregandoCep = false,
}) => {
  const [sugestoesCidade, setSugestoesCidade] = useState([]);

  // Pré-preencher a partir do localStorage se o form chegar vazio
  useEffect(() => {
    if (!isOpen) return;
    const vazio =
      !form?.razaoSocial && !form?.cnpj && !form?.email &&
      !form?.cep && !form?.endereco && !form?.cidade;
    if (vazio) {
      try {
        const draft = JSON.parse(localStorage.getItem("novaEmpresaDraft") || "null");
        if (draft) setForm((f) => ({ ...f, ...draft }));
      } catch {}
    }
  }, [isOpen, form, setForm]);

  // Atualiza IBGE quando cidade/estado mudam
  useEffect(() => {
    async function atualizarIBGE() {
      if (form.cidade && form.estado && form.estado.length === 2) {
        const codigoIBGE = await buscarCodigoIBGE(form.cidade, form.estado);
        if (codigoIBGE && codigoIBGE !== form.codigoIBGE) {
          setForm((f) => ({ ...f, codigoIBGE }));
        }
      }
    }
    atualizarIBGE();
  }, [form.cidade, form.estado, form.codigoIBGE, setForm]);

  // Busca endereço e IBGE pelo CEP (onBlur)
  const handleCepBlur = async () => {
    const raw = (form.cep || "").replace(/\D/g, "");
    if (raw.length === 8) {
      try {
        const res = await fetch(`https://viacep.com.br/ws/${raw}/json/`);
        const data = await res.json();
        if (!data.erro) {
          setForm((f) => ({
            ...f,
            endereco: data.logradouro || "",
            bairro: data.bairro || "",
            cidade: data.localidade || "",
            estado: data.uf || "",
          }));
          const codigoIBGE = await buscarCodigoIBGE(data.localidade, data.uf);
          setForm((f) => ({ ...f, codigoIBGE }));
        }
      } catch {}
    }
  };

  // Autocomplete cidades
  const handleCidadeChange = async (e) => {
    const valor = e.target.value;
    setForm((f) => ({ ...f, cidade: valor, codigoIBGE: "" }));
    setSugestoesCidade([]);
    if (valor.length >= 3 && (form.estado || "").length === 2) {
      try {
        const res = await fetch(`https://brasilapi.com.br/api/ibge/municipios/v1/${(form.estado || "").toLowerCase()}`);
        const cidades = await res.json();
        if (Array.isArray(cidades)) {
          setSugestoesCidade(
            cidades
              .filter((c) => normalizarCidade(c.nome).startsWith(normalizarCidade(valor)))
              .map((c) => c.nome)
          );
        }
      } catch {}
    }
  };

  const handleSelecionaCidade = (nome) => {
    setForm((f) => ({ ...f, cidade: nome }));
    setSugestoesCidade([]);
  };

  const set = useCallback(
    (field, value) => setForm((f) => ({ ...f, [field]: value })),
    [setForm]
  );

  if (!isOpen) return null;

  // 🔹 “Isento” agora é derivado do próprio form (sem estado paralelo)
  const ieIsento =
    (form.inscricaoEstadual || "").toUpperCase() === "ISENTO" || !form.inscricaoEstadual;

  // fechar ao clicar fora
  const closeOnBackdrop = (e) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  // fechar com ESC
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-[2px] animate-fadein"
      onClick={closeOnBackdrop}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-3xl shadow-2xl w-full max-w-4xl relative animate-modalSlideIn overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="max-h-[94vh] flex flex-col">
          {/* Header */}
          <div className="p-7 pb-0 relative">
            <button
              type="button"
              className="absolute right-8 top-7 text-gray-500 hover:text-blue-700 dark:text-gray-200 dark:hover:text-white text-base font-medium"
              onClick={onClose}
            >
              Fechar
            </button>
            <h2 className="text-2xl font-bold mb-6 text-blue-600 dark:text-white">
              {editandoId ? "Editar Empresa" : "Nova Empresa"}
            </h2>
          </div>

          {/* Body (rolável) */}
          <form
            onSubmit={(e) => { e.preventDefault(); onSave?.(); }}
            className="px-7 pb-6 space-y-6 overflow-y-auto flex-1"
          >
            {/* Dados da Empresa */}
            <div className="bg-[#f7faff] dark:bg-gray-800 rounded-xl px-6 py-5 shadow-inner mb-2">
              <h3 className="font-semibold text-[#004072] dark:text-white mb-4 flex items-center gap-2">
                <FaBuilding /> Dados da Empresa
              </h3>
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-semibold mb-1 dark:text-gray-200">Razão Social</label>
                  <InputIcon
                    icon={<FaBuilding />}
                    placeholder="Razão Social"
                    value={form.razaoSocial || ""}
                    onChange={(e) => set("razaoSocial", e.target.value)}
                    className="border rounded-xl px-4 py-3 w-full focus:ring-2 focus:ring-[#004AAD] transition bg-white dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                  />
                  {erros.razaoSocial && <span className="text-red-500 text-xs">{erros.razaoSocial}</span>}
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-1 dark:text-gray-200">Nome Fantasia</label>
                  <InputIcon
                    icon={<FaStore />}
                    placeholder="Nome Fantasia"
                    value={form.nomeFantasia || ""}
                    onChange={(e) => set("nomeFantasia", e.target.value)}
                    className="border rounded-xl px-4 py-3 w-full focus:ring-2 focus:ring-[#004AAD] transition bg-white dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                  />
                  {erros.nomeFantasia && <span className="text-red-500 text-xs">{erros.nomeFantasia}</span>}
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-1 dark:text-gray-200">Regime Tributário</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-400">
                      <FaClipboardList />
                    </span>
                    <select
                      className="pl-10 border rounded-xl px-4 py-3 w-full focus:ring-2 focus:ring-[#004AAD] transition bg-white dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                      value={form.regimeTributario || "1"}
                      onChange={(e) => set("regimeTributario", e.target.value)}
                    >
                      {regimes.map((r) => (
                        <option value={r.value} key={r.value}>{r.label}</option>
                      ))}
                    </select>
                  </div>
                  {erros.regimeTributario && <span className="text-red-500 text-xs">{erros.regimeTributario}</span>}
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-1 dark:text-gray-200">CNPJ</label>
                  <InputIcon
                    icon={<FaBarcode />}
                    mask="00.000.000/0000-00"
                    placeholder="CNPJ"
                    value={form.cnpj || ""}
                    onAccept={(value) => set("cnpj", value)}
                    className="border rounded-xl px-4 py-3 w-full focus:ring-2 focus:ring-[#004AAD] transition bg-white dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                  />
                  {erros.cnpj && <span className="text-red-500 text-xs">{erros.cnpj}</span>}
                </div>

                {/* Inscrição Estadual */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold mb-1 dark:text-gray-200">
                    Inscrição Estadual <span className="text-red-600">*</span>
                  </label>
                  <div className="flex items-center gap-5 mb-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="ie_isento"
                        checked={ieIsento}
                        onChange={() => set("inscricaoEstadual", "ISENTO")}
                        className="accent-[#004072]"
                      />
                      <span>Isento</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="ie_isento"
                        checked={!ieIsento}
                        onChange={() => set("inscricaoEstadual", "")}
                        className="accent-[#004072]"
                      />
                      <span>Possui IE</span>
                    </label>
                  </div>

                  {!ieIsento ? (
                    <InputIcon
                      icon={<FaHashtag />}
                      placeholder="Digite o número da IE"
                      value={form.inscricaoEstadual || ""}
                      onChange={(e) => set("inscricaoEstadual", e.target.value.replace(/\D/g, ""))}
                      className="border rounded-xl px-4 py-3 w-full focus:ring-2 focus:ring-[#004AAD] transition bg-white dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                    />
                  ) : (
                    <InputIcon
                      icon={<FaHashtag />}
                      placeholder="ISENTO"
                      value="ISENTO"
                      readOnly
                      disabled
                      className="border rounded-xl px-4 py-3 w-full bg-gray-100 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400"
                    />
                  )}
                  {erros.inscricaoEstadual && <span className="text-red-500 text-xs">{erros.inscricaoEstadual}</span>}
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-1 dark:text-gray-200">Inscrição Municipal</label>
                  <InputIcon
                    icon={<FaHashtag />}
                    placeholder="Inscrição Municipal"
                    value={form.inscricaoMunicipal || ""}
                    onChange={(e) => set("inscricaoMunicipal", e.target.value)}
                    className="border rounded-xl px-4 py-3 w-full focus:ring-2 focus:ring-[#004AAD] transition bg-white dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                  />
                  {erros.inscricaoMunicipal && <span className="text-red-500 text-xs">{erros.inscricaoMunicipal}</span>}
                </div>

                {/* Novos campos fiscais */}
                <div>
                  <label className="block text-sm font-semibold mb-1 dark:text-gray-200">CNAE Principal</label>
                  <InputIcon
                    icon={<FaClipboardList />}
                    placeholder="Ex: 4520-0/01"
                    value={form.cnae || ""}
                    onChange={(e) => set("cnae", e.target.value)}
                    className="border rounded-xl px-4 py-3 w-full focus:ring-2 focus:ring-[#004AAD] transition bg-white dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                  />
                  {erros.cnae && <span className="text-red-500 text-xs">{erros.cnae}</span>}
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1 dark:text-gray-200">Código Tributário Municipal</label>
                  <InputIcon
                    icon={<FaClipboardList />}
                    placeholder="Ex: 620910000"
                    value={form.codigoTributarioMunicipio || ""}
                    onChange={(e) => set("codigoTributarioMunicipio", e.target.value)}
                    className="border rounded-xl px-4 py-3 w-full focus:ring-2 focus:ring-[#004AAD] transition bg-white dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                  />
                  {erros.codigoTributarioMunicipio && (
                    <span className="text-red-500 text-xs">{erros.codigoTributarioMunicipio}</span>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1 dark:text-gray-200">Item da Lista de Serviço (LC 116/03)</label>
                  <InputIcon
                    icon={<FaListUl />}
                    placeholder="Ex: 14.01"
                    value={form.itemListaServico || ""}
                    onChange={(e) => set("itemListaServico", e.target.value)}
                    className="border rounded-xl px-4 py-3 w-full focus:ring-2 focus:ring-[#004AAD] transition bg-white dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                  />
                  {erros.itemListaServico && <span className="text-red-500 text-xs">{erros.itemListaServico}</span>}
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-1 dark:text-gray-200">E-mail da empresa</label>
                  <InputIcon
                    icon={<FaEnvelope />}
                    placeholder="E-mail da empresa"
                    type="email"
                    value={form.email || ""}
                    onChange={(e) => set("email", e.target.value)}
                    className="border rounded-xl px-4 py-3 w-full focus:ring-2 focus:ring-[#004AAD] transition bg-white dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                  />
                  {erros.email && <span className="text-red-500 text-xs">{erros.email}</span>}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold mb-1 dark:text-gray-200">Telefone</label>
                  <InputIcon
                    icon={<FaPhone />}
                    mask="(00) 00000-0000"
                    placeholder="Telefone"
                    value={form.telefone || ""}
                    onAccept={(value) => set("telefone", value)}
                    className="border rounded-xl px-4 py-3 w-full focus:ring-2 focus:ring-[#004AAD] transition bg-white dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                  />
                  {erros.telefone && <span className="text-red-500 text-xs">{erros.telefone}</span>}
                </div>
              </div>
            </div>

            {/* Endereço */}
            <div className="bg-[#f7faff] dark:bg-gray-800 rounded-xl px-6 py-5 shadow-inner">
              <h3 className="font-semibold text-[#004072] dark:text-white mb-4 flex items-center gap-2">
                <FaMapMarkerAlt /> Endereço
              </h3>
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-medium mb-1 dark:text-gray-200">CEP</label>
                  <InputIcon
                    icon={<FaMapMarkerAlt />}
                    mask="00000-000"
                    placeholder="Ex: 15994-721"
                    value={form.cep || ""}
                    onAccept={(value) => set("cep", value)}
                    onBlur={handleCepBlur}
                    className="border rounded-xl px-4 py-3 w-full focus:ring-2 focus:ring-[#004AAD] transition bg-white dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                  />
                  {carregandoCep && <span className="text-blue-600 dark:text-blue-300 text-xs">Buscando endereço...</span>}
                  {erros.cep && <span className="text-red-500 text-xs">{erros.cep}</span>}
                </div>

                <div>
                  <label className="block text-xs font-medium mb-1 dark:text-gray-200">Número</label>
                  <InputIcon
                    icon={<FaHome />}
                    placeholder="Número"
                    value={form.numero || ""}
                    onChange={(e) => set("numero", e.target.value)}
                    className="border rounded-xl px-4 py-3 w-full focus:ring-2 focus:ring-[#004AAD] transition bg-white dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                  />
                  {erros.numero && <span className="text-red-500 text-xs">{erros.numero}</span>}
                </div>

                <div>
                  <label className="block text-xs font-medium mb-1 dark:text-gray-200">Endereço</label>
                  <InputIcon
                    icon={<FaHome />}
                    placeholder="Endereço"
                    value={form.endereco || ""}
                    onChange={(e) => set("endereco", e.target.value)}
                    className="border rounded-xl px-4 py-3 w-full focus:ring-2 focus:ring-[#004AAD] transition bg-white dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                  />
                  {erros.endereco && <span className="text-red-500 text-xs">{erros.endereco}</span>}
                </div>

                <div>
                  <label className="block text-xs font-medium mb-1 dark:text-gray-200">Complemento</label>
                  <InputIcon
                    icon={<FaRegStickyNote />}
                    placeholder="Ex: Fundos"
                    value={form.complemento || ""}
                    onChange={(e) => set("complemento", e.target.value)}
                    className="border rounded-xl px-4 py-3 w-full focus:ring-2 focus:ring-[#004AAD] transition bg-white dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium mb-1 dark:text-gray-200">Bairro</label>
                  <InputIcon
                    icon={<FaCity />}
                    placeholder="Bairro"
                    value={form.bairro || ""}
                    onChange={(e) => set("bairro", e.target.value)}
                    className="border rounded-xl px-4 py-3 w-full focus:ring-2 focus:ring-[#004AAD] transition bg-white dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                  />
                  {erros.bairro && <span className="text-red-500 text-xs">{erros.bairro}</span>}
                </div>

                {/* Cidade com sugestões */}
                <div className="relative">
                  <label className="block text-xs font-medium mb-1 dark:text-gray-200">Cidade</label>
                  <InputIcon
                    icon={<FaCity />}
                    placeholder="Cidade"
                    value={form.cidade || ""}
                    onChange={handleCidadeChange}
                    autoComplete="off"
                    className="border rounded-xl px-4 py-3 w-full focus:ring-2 focus:ring-[#004AAD] transition bg-white dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                  />
                  {sugestoesCidade.length > 0 && (
                    <div className="absolute z-20 bg-white dark:bg-gray-900 border dark:border-gray-700 w-full mt-1 rounded shadow max-h-32 overflow-y-auto">
                      {sugestoesCidade.map((s, i) => (
                        <div
                          key={i}
                          className="px-3 py-2 hover:bg-blue-50 dark:hover:bg-[#002e50] cursor-pointer"
                          onClick={() => handleSelecionaCidade(s)}
                        >
                          {s}
                        </div>
                      ))}
                    </div>
                  )}
                  {erros.cidade && <span className="text-red-500 text-xs">{erros.cidade}</span>}
                </div>

                <div>
                  <label className="block text-xs font-medium mb-1 dark:text-gray-200">Estado (UF)</label>
                  <InputIcon
                    icon={<FaRegStickyNote />}
                    placeholder="UF"
                    value={form.estado || ""}
                    onChange={(e) => set("estado", e.target.value.toUpperCase().slice(0, 2))}
                    maxLength={2}
                    style={{ textTransform: "uppercase" }}
                    className="border rounded-xl px-4 py-3 w-full focus:ring-2 focus:ring-[#004AAD] transition bg-white dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                  />
                  {erros.estado && <span className="text-red-500 text-xs">{erros.estado}</span>}
                </div>

                <div>
                  <label className="block text-xs font-medium mb-1 dark:text-gray-200">Código IBGE</label>
                  <InputIcon
                    icon={<FaBarcode />}
                    placeholder="Código IBGE"
                    value={form.codigoIBGE || ""}
                    readOnly
                    className="border rounded-xl px-4 py-3 w-full focus:ring-2 focus:ring-[#004AAD] transition bg-gray-100 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300"
                  />
                  {erros.codigoIBGE && <span className="text-red-500 text-xs">{erros.codigoIBGE}</span>}
                </div>
              </div>
            </div>

            {/* Observações */}
            <div>
              <label className="block text-sm font-semibold mb-1 dark:text-gray-200">Observações</label>
              <textarea
                className="border rounded-xl px-4 py-3 w-full bg-gray-50 dark:bg-gray-800 dark:border-gray-700 dark:text-white focus:ring-2 focus:ring-[#004AAD] transition resize-none"
                placeholder="Observações"
                rows={2}
                value={form.obs || ""}
                onChange={(e) => set("obs", e.target.value)}
              />
            </div>

            {/* Login/Senha prefeitura (opcional) */}
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs font-medium mb-1 dark:text-gray-200">Login Prefeitura (se exigido pela cidade)</label>
                <InputIcon
                  icon={<FaKey />}
                  placeholder="Usuário prefeitura"
                  value={form.loginPrefeitura || ""}
                  onChange={(e) => set("loginPrefeitura", e.target.value)}
                  className="border rounded-xl px-4 py-3 w-full focus:ring-2 focus:ring-[#004AAD] transition bg-white dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1 dark:text-gray-200">Senha Prefeitura (se exigido pela cidade)</label>
                <InputIcon
                  icon={<FaKey />}
                  placeholder="Senha prefeitura "
                  type="password"
                  value={form.senhaPrefeitura || ""}
                  onChange={(e) => set("senhaPrefeitura", e.target.value)}
                  className="border rounded-xl px-4 py-3 w-full focus:ring-2 focus:ring-[#004AAD] transition bg-white dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                />
              </div>
            </div>
            <span className="text-xs text-gray-500 mt-1 mb-2 block">
              <b>Atenção:</b> O login e senha da prefeitura só são necessários para cidades que
              exigem acesso extra para emissão de NFS-e. Se não souber, pode deixar em branco e
              preencher depois.
            </span>
          </form>

          {/* Rodapé */}
          <div className="px-7 py-5 bg-white/95 dark:bg-gray-900/95 backdrop-blur border-t border-gray-200 dark:border-gray-800">
            <button
              type="button"
              onClick={() => onSave?.()}
              className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-3.5 font-semibold text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition"
            >
              <FaBuilding /> {editandoId ? "Salvar Alterações" : "Salvar Empresa"}
            </button>
          </div>
        </div>

        {/* Animações */}
        <style>{`
          .animate-modalSlideIn { animation: modalSlideIn 0.35s cubic-bezier(.32,.72,.57,1) both; }
          @keyframes modalSlideIn {
            0% { opacity:0; transform: translateY(60px) scale(.97); }
            100% { opacity:1; transform: translateY(0) scale(1); }
          }
          .animate-fadein { animation: fadein 0.25s; }
          @keyframes fadein { from { opacity: 0; } to { opacity: 1; } }
        `}</style>
      </div>
    </div>
  );
};

export default ModalNovaEmpresa;
